import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { useCurrentProject } from "@/features/projects/current-project";
import { cn } from "@/lib/cn";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { type RecipeCreate, useCreateRecipe } from "./mutations";

export function RecipeCreatePage() {
  const { projectId } = useCurrentProject();
  const navigate = useNavigate();
  const create = useCreateRecipe();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [family, setFamily] = useState<"tabular" | "anomaly" | "forecast" | "">("");

  const descriptionError =
    description.length > 0 && description.length < 20
      ? "Description must be at least 20 characters"
      : null;

  const canSubmit = name.trim().length > 0 && description.length >= 20 && !create.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !projectId) return;

    const selectedFamily = family || null;
    // API requires training_config when family is set — provide minimal defaults
    const defaultConfigs: Record<string, Record<string, unknown>> = {
      tabular: {
        family: "tabular",
        task_type: "classification",
        label: "target",
        mode: "balanced",
      },
      anomaly: { family: "anomaly", mode: "balanced" },
      forecast: {
        family: "forecast",
        label: "value",
        time_col: "timestamp",
        prediction_length: 10,
        mode: "balanced",
      },
    };

    const input: RecipeCreate = {
      project_id: projectId,
      name: name.trim(),
      description,
      body: "",
      family: selectedFamily,
      training_config: selectedFamily ? defaultConfigs[selectedFamily] : undefined,
    };

    const result = await create.mutateAsync(input);
    if (result) {
      navigate({ to: "/recipes/$recipeId", params: { recipeId: result.id } });
    }
  };

  const handleBack = () => {
    navigate({ to: "/recipes" });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeaderV2
        eyebrow="Knowledge"
        title="New Recipe"
        action={
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-code-bg"
            type="button"
          >
            <ArrowLeft className="size-4" />
            Back
          </button>
        }
      />

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="mb-2 block text-sm font-medium text-ink">
            Name
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Customer Churn Prediction"
            className="w-full rounded-md border border-line bg-surface px-4 py-2 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="mb-2 block text-sm font-medium text-ink">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what this recipe does and when to use it…"
            rows={4}
            className={cn(
              "w-full rounded-md border bg-surface px-4 py-2 text-sm text-ink placeholder:text-muted focus:outline-none focus:ring-1",
              descriptionError
                ? "border-status-error focus:border-status-error focus:ring-status-error"
                : "border-line focus:border-accent focus:ring-accent",
            )}
            required
          />
          <div className="mt-1 flex items-center justify-between">
            <span className={cn("text-xs", descriptionError ? "text-status-error" : "text-muted")}>
              {descriptionError || `${description.length} / 20 minimum`}
            </span>
          </div>
        </div>

        {/* Family */}
        <div>
          <label htmlFor="family" className="mb-2 block text-sm font-medium text-ink">
            Family <span className="text-muted">(optional)</span>
          </label>
          <select
            id="family"
            value={family}
            onChange={(e) => setFamily(e.target.value as typeof family)}
            className="w-full rounded-md border border-line bg-surface px-4 py-2 text-sm text-ink focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">None</option>
            <option value="tabular">Tabular</option>
            <option value="anomaly">Anomaly</option>
            <option value="forecast">Forecast</option>
          </select>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={!canSubmit}
            className={cn(
              "rounded-md px-4 py-2 text-sm font-medium transition-colors",
              canSubmit
                ? "bg-accent text-white hover:bg-accent/90"
                : "cursor-not-allowed bg-muted/20 text-muted",
            )}
          >
            {create.isPending ? "Creating…" : "Create recipe"}
          </button>
          <button
            type="button"
            onClick={handleBack}
            className="rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-code-bg"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
